import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.commitments.getCommitments;

type ResponseData = NonNullable<ApiResponse<typeof apiFn>['commitments']>;

const COMMITMENTS_LIST_QUERY_KEY = 'commitments-list';

export const getCommitmentsQueryKey = () =>
  [COMMITMENTS_LIST_QUERY_KEY] as const;

const getCommitmentsParamQueryKey = ({
  includeUsage,
  withInstanceTypes,
}: {
  includeUsage: boolean;
  withInstanceTypes: boolean;
}) => [COMMITMENTS_LIST_QUERY_KEY, includeUsage, withInstanceTypes] as const;

const getCommitmentsQueryOptions = ({
  includeUsage,
  withInstanceTypes,
  enabled,
}: {
  includeUsage: boolean;
  withInstanceTypes: boolean;
  enabled?: UseCommitmentsQueryArgs<unknown>['enabled'];
}) =>
  queryOptions({
    queryKey: getCommitmentsParamQueryKey({ includeUsage, withInstanceTypes }),
    queryFn: async () => {
      const { data } = await apiFn({
        includeUsage,
        includeUsagePerClusters: includeUsage,
        includeUsagePerInstanceTypes: withInstanceTypes,
      });
      return data.commitments || [];
    },
    enabled,
  });

type UseCommitmentsQueryArgs<SELECT> = QueryOptions<ResponseData, SELECT> & {
  includeUsage?: boolean;
  withInstanceTypes?: boolean;
};

export const useCommitmentsQuery = <SELECT = ResponseData>({
  includeUsage = true,
  withInstanceTypes = false,
  enabled,
  select,
}: UseCommitmentsQueryArgs<SELECT> = {}) => {
  return useQuery({
    ...getCommitmentsQueryOptions({ includeUsage, withInstanceTypes, enabled }),
    select,
  });
};
